// =====================================================
// DASHBOARD STATS — Origine des PI (RUN 5)
// =====================================================
// Section « Origine des préinscrits » du cadrage 07/07. Fonctions pures,
// testables en vitest, sans dépendance React.
//
// BESOIN MÉTIER : d'où viennent les pré-inscrits (canal d'acquisition) ? Maxime
// PRÉFÈRE LE VISUEL (barres horizontales) ; le tableau récap N-1 est « moins
// intéressant » → basse priorité (inclus car peu coûteux). PAS de carte de
// France (déjà dans l'espace participant — exclusion transcript).
//
// ── ATTRIBUTION (schéma réel probé) ──────────────────────────────────────────
//   L'origine vit au niveau CONTACT : contacts.origin_id → origins(label,color),
//   renseignée à la création depuis le formulaire (forms.origin_id). Un PI =
//   équipage (crews) ; l'origine d'un PI = origin_id du contact PILOTE de
//   l'équipage (role='pilot'), à défaut d'un membre quelconque. Lien via
//   crew_members(crew_id, contact_id, role).
//
// ── HONNÊTETÉ SEED ───────────────────────────────────────────────────────────
//   origin_id peut être partiellement renseigné → on expose la COUVERTURE
//   (part des PI avec une origine résolue). Les PI sans origine forment une
//   tranche « Origine inconnue » explicite (jamais masquée silencieusement).
//
// ── PILOTAGE ─────────────────────────────────────────────────────────────────
//   Scopé à l'édition N + fenêtre Période (crews.created_at), cohérent avec le
//   KPI PI. N-1 : même fenêtre brute (comme l'entonnoir de conversion RUN 3).

import { isPiCrew } from './nomenclature';
import { inWindow } from './periods';
import type { DateWindow, EditionRef, RawRows } from './types';

/** Une tranche d'origine (une barre + une ligne de tableau). */
export interface OriginSlice {
  /** origins.id, ou null pour « Origine inconnue ». */
  originId: string | null;
  /** origins.name (slug) ou 'inconnu'. */
  name: string;
  /** Libellé affiché (origins.label ou « Origine inconnue »). */
  label: string;
  /** Couleur de la barre (origins.color ou gris). */
  color: string;
  /** Nombre de PI de ce canal (édition N, fenêtre). */
  count: number;
  /** Part sur le total des PI (0..1). */
  share: number;
  /** Nombre de PI de ce canal en N-1 (null si N-1 indisponible). */
  prevCount: number | null;
}

export interface OriginData {
  /** Tranches triées par count décroissant (« inconnu » toujours en fin). */
  slices: OriginSlice[];
  /** Total PI de l'édition N sur la fenêtre. */
  totalPi: number;
  /** PI dont l'origine est résolue (a un origin_id connu). */
  piWithOrigin: number;
  /** Couverture = piWithOrigin / totalPi (0..1), null si aucun PI. */
  coverage: number | null;
  /** true si une édition N-1 est appariée (colonne N-1 pertinente). */
  hasN1: boolean;
}

export interface OriginParams {
  rows: RawRows;
  edition: EditionRef;
  /** Fenêtre N = plage de dates sélectionnée. */
  window: DateWindow;
  /** Fenêtre N-1 = même plage décalée d'un an. */
  prevWindow: DateWindow;
}

const UNKNOWN_COLOR = '#a1a1aa'; // zinc-400

/**
 * Résout l'origin_id d'un équipage : contact pilote en priorité, sinon 1er
 * membre porteur d'un origin_id.
 */
function crewOriginId(
  crewId: string,
  membersByCrew: Map<string, { contactId: string; role: string | null }[]>,
  originByContact: Map<string, string | null>
): string | null {
  const members = membersByCrew.get(crewId) ?? [];
  // Pilote d'abord.
  const pilot = members.find((m) => m.role === 'pilot');
  if (pilot) {
    const o = originByContact.get(pilot.contactId);
    if (o) return o;
  }
  // Sinon premier membre ayant une origine.
  for (const m of members) {
    const o = originByContact.get(m.contactId);
    if (o) return o;
  }
  return null;
}

/** Compte les PI par origin_id d'une édition sur une fenêtre. Map originId|null → count. */
function piCountsByOrigin(
  rows: RawRows,
  editionId: string,
  w: DateWindow,
  membersByCrew: Map<string, { contactId: string; role: string | null }[]>,
  originByContact: Map<string, string | null>
): { counts: Map<string | null, number>; total: number; withOrigin: number } {
  const counts = new Map<string | null, number>();
  let total = 0;
  let withOrigin = 0;
  rows.crews.forEach((c) => {
    if (c.product_id !== editionId || !isPiCrew(c) || !inWindow(c.created_at, w)) return;
    total += 1;
    const oid = crewOriginId(c.id, membersByCrew, originByContact);
    if (oid) withOrigin += 1;
    counts.set(oid, (counts.get(oid) ?? 0) + 1);
  });
  return { counts, total, withOrigin };
}

/**
 * Origine des PI pour l'édition N (+ N-1 brut). Visuel barres horizontales +
 * tableau récap. Honnête sur la couverture origin_id.
 */
export function computeOrigin({
  rows,
  edition,
  window: w,
  prevWindow: wPrev,
}: OriginParams): OriginData {
  // Index membres par équipage + origine par contact.
  const membersByCrew = new Map<string, { contactId: string; role: string | null }[]>();
  (rows.crewMembers ?? []).forEach((m) => {
    const arr = membersByCrew.get(m.crew_id);
    const entry = { contactId: m.contact_id, role: m.role };
    if (arr) arr.push(entry);
    else membersByCrew.set(m.crew_id, [entry]);
  });
  const originByContact = new Map<string, string | null>(
    (rows.memberContacts ?? []).map((c) => [c.id, c.origin_id ?? null])
  );
  const originsById = new Map((rows.origins ?? []).map((o) => [o.id, o]));

  const n = piCountsByOrigin(rows, edition.id, w, membersByCrew, originByContact);
  const prevId = edition.prevEditionId;
  const p = prevId ? piCountsByOrigin(rows, prevId, wPrev, membersByCrew, originByContact) : null;

  const total = n.total;
  const slices: OriginSlice[] = [];
  n.counts.forEach((count, oid) => {
    const origin = oid ? originsById.get(oid) : undefined;
    slices.push({
      originId: oid,
      name: origin?.name ?? 'inconnu',
      label: origin?.label ?? 'Origine inconnue',
      color: origin?.color ?? UNKNOWN_COLOR,
      count,
      share: total > 0 ? count / total : 0,
      prevCount: p ? (p.counts.get(oid) ?? 0) : null,
    });
  });

  // Tri : count décroissant, « inconnu » (originId null) toujours en dernier.
  slices.sort((a, b) => {
    if (a.originId == null) return 1;
    if (b.originId == null) return -1;
    return b.count - a.count;
  });

  return {
    slices,
    totalPi: total,
    piWithOrigin: n.withOrigin,
    coverage: total > 0 ? n.withOrigin / total : null,
    hasN1: !!prevId,
  };
}
